import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, SystemStatus } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';

/**
 * Operational health (docs/deployment.md §7).
 *
 * Almost read-only: this is the screen someone opens when they suspect
 * something is wrong, so the only thing it can change is whether an alert has
 * been *seen* (D-109). Acknowledging says a person looked, not that the thing
 * is fixed — the row and its history stay exactly as they were.
 */

const severityTone = (severity: string): Tone => {
  switch (severity) {
    case 'CRITICAL':
    case 'ERROR':
      return 'bad';
    case 'WARNING':
      return 'warn';
    default:
      return 'neutral';
  }
};

function StatusCard({
  label,
  value,
  tone,
  detail,
}: {
  label: string;
  value: string;
  tone: Tone;
  detail: string;
}) {
  const accent: Record<Tone, string> = {
    ok: 'text-ok',
    warn: 'text-warn',
    bad: 'text-bad',
    accent: 'text-accent',
    neutral: 'text-ink-900',
  };
  return (
    <div className="flex-1 rounded-lg border border-line bg-surface px-5 py-4">
      <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
      <p className={`tabular mt-1 text-3xl font-bold ${accent[tone]}`}>{value}</p>
      <p className="mt-1 text-sm text-ink-600">{detail}</p>
    </div>
  );
}

export default function SystemPage() {
  const can = usePermissions();
  const status = useQuery<SystemStatus, ApiError>({
    queryKey: ['system'],
    queryFn: endpoints.system,
    // This is a health screen; stale numbers here are worse than a refetch.
    refetchInterval: 30_000,
  });

  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['system'] });
  const acknowledge = useMutation({ mutationFn: (id: string) => endpoints.acknowledgeAlert(id), onSuccess: refresh });
  const acknowledgeAll = useMutation({ mutationFn: () => endpoints.acknowledgeAllAlerts(), onSuccess: refresh });

  if (!can('admin.access')) return <NotPermitted permission="admin.access" />;
  if (status.error) return <ErrorNote>{status.error.message}</ErrorNote>;

  const data = status.data;
  const backlog = data?.outbox.undispatched ?? 0;
  const open = data?.alerts.open ?? 0;
  const cronStale = data?.cron.stale ?? true;

  const bySeverity = Object.entries(data?.alerts.open_by_severity ?? {})
    .map(([severity, n]) => `${n} ${severity.toLowerCase()}`)
    .join(', ');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4">
        <StatusCard
          label="Outbox backlog"
          value={status.isPending ? '—' : String(backlog)}
          tone={backlog === 0 ? 'ok' : backlog > 50 ? 'bad' : 'warn'}
          detail={
            backlog === 0
              ? 'Every event has been dispatched.'
              : `${data?.outbox.retrying ?? 0} retrying · oldest ${formatWhen(data?.outbox.oldest_undispatched_at ?? null)}`
          }
        />
        <StatusCard
          label="Open alerts"
          value={status.isPending ? '—' : String(open)}
          tone={open === 0 ? 'ok' : 'bad'}
          detail={open === 0 ? 'Nothing unacknowledged.' : bySeverity}
        />
        <StatusCard
          label="Cron"
          value={status.isPending ? '—' : cronStale ? 'Stale' : 'Running'}
          tone={cronStale ? 'bad' : 'ok'}
          detail={
            data?.cron.last_run_at
              ? `Last run ${formatWhen(data.cron.last_run_at)}`
              : 'No run recorded yet. A stopped cron is silent — the outbox simply stops draining.'
          }
        />
      </div>

      <Panel
        title="Recent alerts"
        description="Raised by money-adjacent failures, dead-lettered messages and drift checks. Marking one seen says somebody looked; it does not fix anything."
        actions={
          (data?.alerts.open ?? 0) > 0 ? (
            <Button disabled={acknowledgeAll.isPending} onClick={() => acknowledgeAll.mutate()}>
              {acknowledgeAll.isPending ? 'Marking…' : `Mark all ${data!.alerts.open} seen`}
            </Button>
          ) : null
        }
      >
        <Table headers={['Severity', 'Code', 'Message', 'Subject', 'Raised', 'State']}>
          {status.isPending ? (
            <EmptyRow columns={6}>Loading…</EmptyRow>
          ) : (data?.alerts.recent.length ?? 0) === 0 ? (
            <EmptyRow columns={6}>No alerts have been raised.</EmptyRow>
          ) : (
            data!.alerts.recent.map((alert) => (
              <tr key={alert.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Badge tone={severityTone(alert.severity)}>{alert.severity}</Badge>
                </td>
                <td className="px-5 py-3 text-sm font-medium text-ink-900">{alert.code}</td>
                <td className="px-5 py-3 text-sm text-ink-600">{alert.message}</td>
                <td className="px-5 py-3 text-xs text-ink-400">
                  {alert.subject_type ? `${alert.subject_type} ${alert.subject_id ?? ''}`.trim() : '—'}
                </td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(alert.created_at)}</td>
                <td className="px-5 py-3">
                  {alert.acknowledged_at ? (
                    <span className="text-xs text-ink-400">seen {formatWhen(alert.acknowledged_at)}</span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Badge tone="warn">open</Badge>
                      <Button disabled={acknowledge.isPending} onClick={() => acknowledge.mutate(alert.id)}>
                        Mark seen
                      </Button>
                    </span>
                  )}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
